import { useEffect, useState } from "react";

import { collection, doc, limit, onSnapshot, orderBy, query, updateDoc } from "firebase/firestore";
import { Link, useSearchParams } from "react-router-dom";
import { Avatar, Badge, Box, Button, Divider, Flex, Group, Loader, Paper, ScrollArea, SegmentedControl, Stack, Text, TextInput, Title, UnstyledButton } from "@mantine/core";
import { notifications } from "@mantine/notifications";

import { useFirestore } from "../../helpers/firebaseContext";
import useFirestoreCollection from "../../helpers/useFirestoreCollection";
import useFirestoreDoc from "../../helpers/useFirestoreDoc";
import { TagBadge } from "../../components/tag_badge";
import { Customer, WhatsappThread } from "../../types";
import { ChatThread } from "../customers/customer_whatsapp";
import { InboxView, mergeThreadLists, threadActivityAt, threadMatchesFilter, threadSnippet } from "./threadList";

const threadDisplayName = (thread: WhatsappThread) =>
  thread.customerNames?.[0]?.name_en ||
  thread.customerNames?.[0]?.name_ar ||
  thread.profileName ||
  thread.mobile;

const formatWhen = (thread: WhatsappThread) => {
  const date = threadActivityAt(thread)?.toDate();
  if (!date) return "";
  const today = new Date();
  return date.toDateString() === today.toDateString()
    ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString();
};

function ThreadRow({ thread, active, onSelect }: { thread: WhatsappThread, active: boolean, onSelect: () => void }) {
  const unknown = !thread.customerIds?.length;
  const snippet = threadSnippet(thread);

  return (
    <UnstyledButton
      onClick={onSelect}
      w="100%"
      p="sm"
      bg={active ? "var(--mantine-color-blue-light)" : undefined}
      style={{ borderRadius: "var(--mantine-radius-sm)" }}
    >
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="sm" fw={600} truncate>{threadDisplayName(thread)}</Text>
            {unknown && <Badge color="orange" size="xs" variant="light">Unknown</Badge>}
          </Group>
          <Group gap={4} wrap="nowrap">
            {snippet.template && <Badge size="xs" variant="light" color="grape" style={{ flexShrink: 0 }}>Template</Badge>}
            <Text size="xs" c="dimmed" lineClamp={1}>
              {snippet.outbound && !snippet.template && "You: "}
              {snippet.text}
            </Text>
          </Group>
        </Stack>
        <Stack gap={4} align="flex-end">
          <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>{formatWhen(thread)}</Text>
          {!!thread.unreadCount && <Badge color="green" size="sm" circle>{thread.unreadCount}</Badge>}
        </Stack>
      </Group>
    </UnstyledButton>
  );
}

// Helpdesk context card above the chat. Rendered with key={customerID} because
// useFirestoreDoc doesn't resubscribe when its ref changes.
function CustomerHeaderCard({ customerID }: { customerID: string }) {
  const db = useFirestore();
  const { loading, data: customer } = useFirestoreDoc<Customer>(doc(db, "customers", customerID), true);

  if (loading || !customer) return <Loader size="sm" />;

  const paciPhoto = typeof customer.paciData?.PersonalData?.Photo === "string" ? customer.paciData.PersonalData.Photo : undefined;

  //tags with a service field are product tags (funds, portfolios) — status tags (kycStatus, risk, ...) don't have one
  const productTags = Object.entries(customer.tags ?? {})
    .filter(([, tag]) => !!tag?.service)
    .sort(([a], [b]) => a.localeCompare(b));

  return (
    <Paper withBorder p="sm" radius="md">
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Group wrap="nowrap" align="flex-start">
          <Avatar
            src={paciPhoto ? `data:image/png;base64,${paciPhoto}` : undefined}
            name={customer.name_en}
            color="initials"
            size="lg"
            radius="md"
          />
          <Stack gap={2}>
            <Group gap="xs">
              <Text fw={600}>{customer.name_en}</Text>
              <TagBadge tag={customer.tags?.kycStatus} />
              {customer.segment && <Badge variant="light" color="blue">{customer.segment}</Badge>}
            </Group>
            <Text size="sm" c="dimmed">{customer.name_ar}</Text>
            <Group gap="md">
              {customer.civilID && <Text size="xs" c="dimmed">Civil ID: {customer.civilID}</Text>}
              {customer.mobile && <Text size="xs" c="dimmed">Mobile: {customer.mobile}</Text>}
              {customer.email && <Text size="xs" c="dimmed">{customer.email}</Text>}
            </Group>
            <Group gap="md">
              {customer.kfhtrade_id && <Text size="xs" c="dimmed">KFH Trade: {customer.kfhtrade_id}</Text>}
              {customer.rm_email && <Text size="xs" c="dimmed">RM: {customer.rm_email}</Text>}
            </Group>
            {productTags.length > 0 && (
              <Group gap={4}>
                {productTags.map(([key, tag]) => <TagBadge key={key} tag={tag} />)}
              </Group>
            )}
          </Stack>
        </Group>
        <Button component={Link} to={`/customers/${customerID}`} variant="light" size="xs">
          Open customer ↗
        </Button>
      </Group>
    </Paper>
  );
}

export default function WhatsappInbox() {
  const db = useFirestore();
  const [searchParams, setSearchParams] = useSearchParams();

  const threadsQuery = query(
    collection(db, "whatsapp_threads"),
    orderBy("lastMessageAt", "desc"),
    limit(200),
  );
  const { loading, data } = useFirestoreCollection(threadsQuery, true);
  const chatThreads = (data ?? []) as WhatsappThread[];

  //template sends never set lastMessageAt (see logTemplateSendToWhatsappThread), so the
  //default "Conversations" view stays free of campaign noise; "All sends" adds a second
  //query ordered by lastTemplateAt and merges the two, so staff can confirm a KYC link or
  //notification actually went out without waiting for the client to reply
  const [view, setView] = useState<InboxView>("conversations");
  const [filter, setFilter] = useState("");
  const [templateThreads, setTemplateThreads] = useState<WhatsappThread[] | undefined>(undefined);
  useEffect(() => {
    if (view !== "all") return;
    const templateQuery = query(collection(db, "whatsapp_threads"), orderBy("lastTemplateAt", "desc"), limit(200));
    return onSnapshot(templateQuery, (snapshot) => {
      setTemplateThreads(snapshot.docs.map((d) => ({ ...(d.data() as WhatsappThread), id: d.id })));
    });
  }, [db, view]);

  const knownThreads = mergeThreadLists(chatThreads, templateThreads ?? []);
  const threads = (view === "all" ? knownThreads : chatThreads).filter((thread) => threadMatchesFilter(thread, filter));
  const templatesLoading = view === "all" && templateThreads === undefined;

  //the URL is the single source of truth for the open conversation, so threads stay linkable
  const selectedThreadID = searchParams.get("thread");
  const selectThread = (threadID: string | null) =>
    setSearchParams(threadID ? { thread: threadID } : {}, { replace: true });

  const [unreadRequest, setUnreadRequest] = useState<string | null>(null);

  useEffect(() => {
    if (!unreadRequest) return;
    updateDoc(doc(db, "whatsapp_threads", unreadRequest), { unreadCount: 1 })
      .then(() => {
        notifications.show({
          title: "Marked as unread",
          message: "The conversation was marked as unread.",
          color: "green",
        });
      })
      .catch((error) => {
        console.error("Failed to mark WhatsApp conversation as unread:", error);
        notifications.show({
          title: "Failed to mark as unread",
          message: error instanceof Error ? error.message : "Please try again.",
          color: "red",
        });
      });
    setUnreadRequest(null);
  }, [db, unreadRequest]);

  if (loading || data === undefined) return <Loader />;

  //nothing is open until the user picks a thread: opening one marks it read, so we must never
  //auto-select on behalf of whoever happens to land on the inbox
  const activeThread = knownThreads.find((t) => t.id === selectedThreadID);

  const markAsUnread = () => {
    if (!activeThread?.id) return;
    //both updates batch into one render, so the write above happens with the chat already
    //closed — otherwise ChatThread's read-on-open would clear the counter right back
    setUnreadRequest(activeThread.id);
    selectThread(null);
  };

  return (
    <>
      <Group justify="space-between" mb="sm" wrap="wrap">
        <Title order={3}>WhatsApp</Title>
        <Group gap="sm">
          <TextInput
            size="xs"
            w={240}
            placeholder="Filter by name or number"
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
          />
          <SegmentedControl
            size="xs"
            value={view}
            onChange={(value) => setView(value as InboxView)}
            data={[
              { value: "conversations", label: "Conversations" },
              { value: "all", label: "All sends" },
            ]}
          />
        </Group>
      </Group>
      {(
        <Flex gap="md" align="stretch">
          <Paper withBorder w={340} style={{ flexShrink: 0 }}>
            <ScrollArea h="calc(100vh - 160px)">
              <Stack gap={0} p={4}>
                {templatesLoading ? (
                  <Loader size="sm" m="md" />
                ) : threads.length === 0 ? (
                  <Text c="dimmed" size="sm" p="md">
                    {filter.trim()
                      ? "No threads match the filter."
                      : view === "all"
                        ? "No WhatsApp activity yet."
                        : "No conversations yet. Clients appear here when they message the company WhatsApp number; switch to All sends to see template-only threads."}
                  </Text>
                ) : threads.map((thread) => (
                  <ThreadRow
                    key={thread.id}
                    thread={thread}
                    active={thread.id === activeThread?.id}
                    onSelect={() => selectThread(thread.id!)}
                  />
                ))}
              </Stack>
            </ScrollArea>
          </Paper>

          <Box style={{ flexGrow: 1, minWidth: 0 }}>
            {activeThread ? (
              <>
                <Stack gap="xs" mb="xs">
                  {activeThread.customerIds?.length ? (
                    activeThread.customerIds.map((customerID) => (
                      <CustomerHeaderCard key={customerID} customerID={customerID} />
                    ))
                  ) : (
                    <Group gap="xs">
                      <Text fw={600}>{threadDisplayName(activeThread)}</Text>
                      <Text size="xs" c="dimmed">{activeThread.mobile}</Text>
                      <Badge color="orange" variant="light">Not matched to a client</Badge>
                    </Group>
                  )}
                  <Group justify="flex-end">
                    <Button variant="light" size="xs" onClick={markAsUnread}>
                      Mark as unread
                    </Button>
                  </Group>
                </Stack>
                <Divider mb="sm" />
                <ChatThread key={activeThread.id} thread={activeThread} />
              </>
            ) : (
              <Flex h="calc(100vh - 160px)" align="center" justify="center">
                <Text c="dimmed">Select a conversation to open it. Opening a chat marks it as read.</Text>
              </Flex>
            )}
          </Box>
        </Flex>
      )}
    </>
  );
}
